import type FamilyPortalAPIClient from '../auth/FamilyPortalAPIClient'
import { checkExportStatus } from './api/fastenClient'
import type { EhiExportStatus } from './types'

/** Delay between export status polls while waiting for the webhook to deliver download links. */
export const EXPORT_STATUS_POLL_INTERVAL_MS = 5000
export const EXPORT_STATUS_MAX_POLL_ATTEMPTS = 60

export const EXPORT_POLL_TIMEOUT_MESSAGE =
  'Timed out waiting for the export to finish. Please try triggering the export again.'

export class ExportPollTimeoutError extends Error {
  constructor(message = EXPORT_POLL_TIMEOUT_MESSAGE) {
    super(message)
    this.name = 'ExportPollTimeoutError'
  }
}

type PollOptions = {
  intervalMs?: number
  maxAttempts?: number
  sleep?: (ms: number) => Promise<void>
  signal?: { aborted: boolean }
}

export type PollCancelHandle = {
  readonly signal: { aborted: boolean }
  cancel(): void
}

/** Create a cancellation handle for pollExportStatus. */
export function createPollCancelHandle(): PollCancelHandle {
  const signal = { aborted: false }
  return {
    signal,
    cancel() {
      signal.aborted = true
    },
  }
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

function isAborted(signal: { aborted: boolean } | undefined): boolean {
  return signal?.aborted ?? false
}

/** Poll Fasten export status until download links arrive, the export fails, or attempts are exhausted. */
export async function pollExportStatus(
  client: FamilyPortalAPIClient,
  orgConnectionId: string,
  onStatus: (status: EhiExportStatus) => void,
  options: PollOptions = {}
): Promise<void> {
  const intervalMs = options.intervalMs ?? EXPORT_STATUS_POLL_INTERVAL_MS
  const maxAttempts = options.maxAttempts ?? EXPORT_STATUS_MAX_POLL_ATTEMPTS
  const sleep = options.sleep ?? defaultSleep
  const { signal } = options

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (isAborted(signal)) {
      return
    }
    if (attempt > 0) {
      await sleep(intervalMs)
      if (isAborted(signal)) {
        return
      }
    }
    const next = await checkExportStatus(client, orgConnectionId)
    if (isAborted(signal)) {
      return
    }
    onStatus(next)
    if ((next.downloadLinks?.length ?? 0) > 0) return
    if (next.status === 'failed') return
  }

  if (isAborted(signal)) {
    return
  }

  throw new ExportPollTimeoutError()
}
